import { ChevronDown, ChevronRight, Minus, Plus } from "lucide-react";
import { useState } from "react";

import { DEFAULT_RUN_CAP, RESULTS } from "../../../shared/domain/gameReducer";
import type { PlateAppearanceResult as DomainPlateAppearanceResult } from "../../../shared/domain/gameReducer";
import { batterDestination, validatePlayDraft } from "../../domain/playDraft";
import type { BattingPlayer } from "../../domain/gamePresentation";
import type {
  PlayerListItem,
  RunnerDestination,
} from "../../shared/types/contracts";
import { cn } from "@/shared/lib/cn";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/shared/ui/drawer";
import { RbiAttributionPrompt } from "./RbiAttributionPrompt";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";
import { Spinner } from "../../shared/ui/spinner";

export type PlateAppearanceResult = DomainPlateAppearanceResult;

export interface PendingPlayViewModel {
  r: PlateAppearanceResult;
  decisions: Record<number, RunnerDestination>;
  rbi: number;
}

interface BattingControlsProps {
  players: PlayerListItem[];
  battingList: BattingPlayer[];
  bases: Array<string | null>;
  inningRuns: number;
  currentRunCap: number;
  batterIndex: number;
  batter: BattingPlayer | null;
  nextBatters: BattingPlayer[];
  isJerseyBatter: boolean;
  jerseyCaption: string;
  pendingRbi: { batterId: string | null; runs: number } | null;
  pendingPlay: PendingPlayViewModel | null;
  showLegend: boolean;
  gameId?: string | null;
  actionStatus?: IntentStatusView;
  onAdvanceRunner: (baseIndex: number) => void;
  onRemoveRunner: (baseIndex: number) => void;
  onApplyRbi: (runs: number) => boolean | Promise<boolean>;
  onSetDecision: (baseIndex: number, destination: RunnerDestination) => void;
  onAdjustPendingRbi: (delta: number) => void;
  onApplyPlay: (
    result: PlateAppearanceResult,
    decisions: Record<number, RunnerDestination>,
    rbi: number,
  ) => void | Promise<unknown>;
  onCancelPlay: () => void;
  onRecordResult: (result: PlateAppearanceResult) => void;
  onEndHalfInning: () => void;
  onToggleLegend: () => void;
}

interface PlayLogProps {
  log: string[];
  open: boolean;
  onToggle: () => void;
}

const RESULT_LIST = Object.values(RESULTS) as PlateAppearanceResult[];
const BASE_NAMES = ["1st", "2nd", "3rd"] as const;
const BASE_SLOTS = [
  { base: 1, className: "top-[3.55rem] left-[5.75rem]" },
  { base: 2, className: "top-3.5 left-[3.05rem]" },
  { base: 3, className: "top-[3.55rem] left-1.5" },
] as const;
const RUNNER_SLOTS = [
  "top-[5.9rem] left-[5.2rem]",
  "-top-0.5 left-[2.45rem]",
  "top-[5.9rem] -left-0.5",
] as const;

function baseName(baseIndex: number): string {
  return BASE_NAMES[baseIndex] ?? "";
}

function defaultCapRunnerBaseIndex(
  bases: Array<string | null>,
  inningRuns: number,
  currentRunCap: number,
): number | null {
  if (currentRunCap !== DEFAULT_RUN_CAP) return null;

  let runNumber = inningRuns;
  for (let baseIndex = bases.length - 1; baseIndex >= 0; baseIndex -= 1) {
    if (!bases[baseIndex]) continue;
    runNumber += 1;
    if (runNumber === DEFAULT_RUN_CAP) return baseIndex;
  }

  return null;
}

function isDefaultCapBatter(
  bases: Array<string | null>,
  inningRuns: number,
  currentRunCap: number,
): boolean {
  return (
    currentRunCap === DEFAULT_RUN_CAP &&
    inningRuns + bases.filter(Boolean).length + 1 === DEFAULT_RUN_CAP
  );
}

function resultVariant(
  result: PlateAppearanceResult,
): "scoringHit" | "scoringOut" | "scoringReach" {
  if (result.hit) return "scoringHit";
  if (result.out) return "scoringOut";
  return "scoringReach";
}

export function BattingControls({
  players,
  battingList,
  bases,
  inningRuns,
  currentRunCap,
  batterIndex,
  batter,
  nextBatters,
  isJerseyBatter,
  jerseyCaption,
  pendingRbi,
  pendingPlay,
  showLegend,
  gameId = null,
  actionStatus = EMPTY_INTENT_STATUS,
  onAdvanceRunner,
  onRemoveRunner,
  onApplyRbi,
  onSetDecision,
  onAdjustPendingRbi,
  onApplyPlay,
  onCancelPlay,
  onRecordResult,
  onEndHalfInning,
  onToggleLegend,
}: BattingControlsProps) {
  const hasRunners = bases.some(Boolean);
  const capRunnerBaseIndex = defaultCapRunnerBaseIndex(
    bases,
    inningRuns,
    currentRunCap,
  );
  const batterIsCapRun = isDefaultCapBatter(bases, inningRuns, currentRunCap);
  const gamePendingCount = gameId
    ? actionStatus.countFor(actionKeys.gameGroup(gameId))
    : 0;
  const gameUnknownCount = gameId
    ? actionStatus.unknownCountFor(actionKeys.gameGroup(gameId))
    : 0;
  const rbiPending = gameId
    ? actionStatus.isPending(actionKeys.rbi(gameId))
    : false;
  const rbiUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.rbi(gameId)) === "outcome_unknown"
    : false;
  const endHalfPending = gameId
    ? actionStatus.isPending(actionKeys.endHalf(gameId))
    : false;
  const endHalfUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.endHalf(gameId)) === "outcome_unknown"
    : false;

  return (
    <>
      <section className="mb-2 flex min-w-0 items-center gap-2 rounded-[var(--radius-lg)] border border-border bg-surface/70 p-2 shadow-[var(--shadow-card)]">
        <div
          className="relative h-[7.25rem] w-[7.75rem] shrink-0"
          aria-label="Base runners"
        >
          {BASE_SLOTS.map(({ base, className }) => {
            const advancing = gameId
              ? actionStatus.isPending(
                  actionKeys.runnerAdvance(gameId, base - 1),
                )
              : false;
            return (
              <button
                key={base}
                type="button"
                onClick={() => onAdvanceRunner(base - 1)}
                disabled={advancing}
                className={cn(
                  "absolute grid size-8 rotate-45 place-items-center rounded-[0.3rem] border-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/70",
                  className,
                  bases[base - 1]
                    ? "border-warning bg-warning"
                    : "border-foreground bg-background",
                )}
                aria-label={
                  capRunnerBaseIndex === base - 1
                    ? `Base ${base} — 6th run — tap to advance runner`
                    : `Base ${base} — tap to advance runner`
                }
                aria-busy={advancing || undefined}
              >
                {advancing ? (
                  <Spinner decorative className="-rotate-45 size-3" />
                ) : capRunnerBaseIndex === base - 1 ? (
                  <span
                    aria-hidden="true"
                    className="-rotate-45 font-mono text-sm font-black text-warning-foreground"
                  >
                    {DEFAULT_RUN_CAP}
                  </span>
                ) : null}
              </button>
            );
          })}
          <div className="absolute top-[6.2rem] left-[3.15rem] h-4 w-7 bg-foreground [clip-path:polygon(0_0,100%_0,100%_60%,50%_100%,0_60%)]" />
          {bases.map((playerId, baseIndex) => {
            const removing = gameId
              ? actionStatus.isPending(
                  actionKeys.runnerRemove(gameId, baseIndex),
                )
              : false;
            return playerId ? (
              <button
                key={baseIndex}
                type="button"
                onClick={() => onRemoveRunner(baseIndex)}
                disabled={removing}
                aria-busy={removing || undefined}
                className={cn(
                  "absolute z-10 max-w-14 truncate rounded px-1.5 py-0.5 text-[0.65rem] leading-tight font-bold text-warning-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/70",
                  "bg-warning",
                  RUNNER_SLOTS[baseIndex],
                )}
                aria-label="Runner — tap for out on bases"
              >
                {removing ? (
                  <Spinner decorative className="size-3" />
                ) : (
                  <>
                    {playerId === "manual"
                      ? "Runner"
                      : players.find((player) => player.id === playerId)
                          ?.name}{" "}
                    ✕
                  </>
                )}
              </button>
            ) : null;
          })}
        </div>

        <div className="min-w-0 flex-1 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <div className="font-mono text-[0.68rem] font-bold tracking-[0.12em] text-warning">
              AT BAT
            </div>
            {batterIsCapRun ? (
              <Badge
                variant="warning"
                className="h-4 border-warning bg-warning px-1.5 text-[0.58rem] tracking-normal text-warning-foreground"
                aria-label={`${batter?.name ?? "Batter"} is the 6th potential run`}
              >
                6th run
              </Badge>
            ) : null}
          </div>
          {isJerseyBatter ? (
            <div
              className="jersey-frame my-1 rounded-[var(--radius-lg)] p-[3px]"
              title="wearing the strikeout jersey"
            >
              <div className="rounded-[calc(var(--radius-lg)-3px)] bg-surface px-2 py-1.5">
                <div className="truncate text-xl leading-tight font-bold">
                  <span className="jersey-wiggle" aria-hidden="true">
                    👕
                  </span>{" "}
                  <span className="jersey-text">{batter?.name}</span>
                  {batter?.num != null ? (
                    <span className="font-mono text-xs text-warning">
                      {" "}
                      #{batter.num}
                    </span>
                  ) : null}
                </div>
                <div className="jersey-text truncate font-mono text-[0.62rem] font-bold tracking-wide">
                  {jerseyCaption}
                </div>
              </div>
            </div>
          ) : (
            <div className="truncate py-1 text-xl leading-tight font-bold">
              {batter?.name}
              {batter?.num != null ? (
                <span className="font-mono text-xs text-warning">
                  {" "}
                  #{batter.num}
                </span>
              ) : null}
            </div>
          )}
          <div className="mt-1 ml-20 text-left">
            <div className="font-mono text-[0.68rem] font-bold tracking-[0.12em] text-foreground-muted">
              Up Next
            </div>
            <ul className="mt-0.5 list-none space-y-0.5 text-xs text-foreground-muted">
              {nextBatters.map((nextBatter, index) => (
                <li
                  key={`${nextBatter.id ?? nextBatter.name}-${index}`}
                  className="truncate"
                >
                  {nextBatter.name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <RbiAttributionPrompt
        pendingRbi={pendingRbi}
        onApplyRbi={onApplyRbi}
        pending={rbiPending}
        unknown={rbiUnknown}
      />

      <div
        aria-live="polite"
        className="flex min-h-5 items-center justify-end gap-1 font-mono text-[0.62rem] font-bold tracking-[0.08em] text-foreground-muted"
      >
        {gamePendingCount ? (
          <>
            <Spinner decorative className="size-3" />
            {gameUnknownCount
              ? `CHECKING ${gameUnknownCount} ACTION${gameUnknownCount === 1 ? "" : "S"}`
              : `${gamePendingCount} ACTION${gamePendingCount === 1 ? "" : "S"} IN FLIGHT`}
          </>
        ) : null}
      </div>

      <div
        className="grid grid-cols-4 gap-1.5"
        data-testid="scoring-result-grid"
      >
        {RESULT_LIST.map((result) => (
          <Button
            key={result.key}
            type="button"
            size="scoring"
            variant={resultVariant(result)}
            disabled={result.key === "FC" && !hasRunners}
            onClick={() => onRecordResult(result)}
          >
            {result.label}
          </Button>
        ))}
        <Button
          type="button"
          size="scoring"
          variant="outline"
          onClick={onEndHalfInning}
          className="min-w-0 overflow-hidden border-dashed"
          pending={endHalfPending}
          pendingLabel={
            <span className="text-xs">
              {endHalfUnknown ? "CHECKING…" : "ENDING…"}
            </span>
          }
        >
          END ½
        </Button>
      </div>

      <button
        type="button"
        onClick={onToggleLegend}
        aria-label={showLegend ? "▾ hide" : "? what do the buttons mean"}
        className="mt-1 inline-flex h-7 items-center gap-1 rounded px-1 font-mono text-[0.68rem] tracking-wide text-foreground-muted outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/70"
      >
        {showLegend ? (
          <>
            <ChevronDown className="size-3" aria-hidden="true" /> hide
          </>
        ) : (
          "? what do the buttons mean"
        )}
      </button>
      {showLegend ? (
        <div className="mb-1 rounded-[var(--radius-sm)] border border-border bg-surface/75 p-2.5 text-xs leading-relaxed text-foreground-muted">
          <b className="text-foreground">1B/2B/3B</b> single · double · triple
          &nbsp; <b className="text-foreground">HR</b> home run
          <br />
          <b className="text-foreground">BB</b> walk (no at-bat charged) &nbsp;{" "}
          <b className="text-foreground">K</b> strikeout
          <br />
          <b className="text-foreground">GO / FO</b> ground out · fly out
          <br />
          <b className="text-foreground">FC</b> fielder&apos;s choice — batter
          safe, out attempted elsewhere
          <br />
          <b className="text-foreground">E</b> error — batter reaches, no hit
          credited
          <br />
          <b className="text-foreground">SF</b> sacrifice fly — out, runner on
          3rd scores, no at-bat charged
          <br />
          <b className="text-foreground">END ½</b> manually end the half-inning
          <br />
          <b className="text-foreground">Diamond</b>: tap a base to advance that
          runner (3rd = scores) · tap a runner&apos;s name ✕ = out on bases
          <br />
          <b className="text-foreground">Open inning</b>: if the ump calls the
          last inning, declare it open — the run cap lifts for that inning
        </div>
      ) : null}

      {pendingPlay ? (
        <PendingPlayDrawer
          players={players}
          battingList={battingList}
          bases={bases}
          batterIndex={batterIndex}
          pendingPlay={pendingPlay}
          onSetDecision={onSetDecision}
          onAdjustPendingRbi={onAdjustPendingRbi}
          onApplyPlay={onApplyPlay}
          onCancelPlay={onCancelPlay}
        />
      ) : null}
    </>
  );
}

function PendingPlayDrawer({
  players,
  battingList,
  bases,
  batterIndex,
  pendingPlay,
  onSetDecision,
  onAdjustPendingRbi,
  onApplyPlay,
  onCancelPlay,
}: Pick<
  BattingControlsProps,
  | "players"
  | "battingList"
  | "bases"
  | "batterIndex"
  | "pendingPlay"
  | "onSetDecision"
  | "onAdjustPendingRbi"
  | "onApplyPlay"
  | "onCancelPlay"
> & { pendingPlay: PendingPlayViewModel }) {
  const [submitting, setSubmitting] = useState(false);
  const batterDest = batterDestination(pendingPlay.r);
  const draftValidation = validatePlayDraft(
    pendingPlay.r,
    pendingPlay.decisions,
  );
  const collision = !draftValidation.valid;
  const batterName =
    battingList[batterIndex % battingList.length]?.name ?? "Batter";

  const confirmPlay = async () => {
    setSubmitting(true);
    try {
      await onApplyPlay(pendingPlay.r, pendingPlay.decisions, pendingPlay.rbi);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open && !submitting) onCancelPlay();
      }}
    >
      <DrawerContent showCloseButton={!submitting}>
        <DrawerHeader>
          <DrawerTitle>
            {batterName}: {pendingPlay.r.label} — confirm runners
          </DrawerTitle>
          <DrawerDescription>
            {!pendingPlay.r.out
              ? `Batter → ${
                  pendingPlay.r.key === "HR"
                    ? "scores 🏠"
                    : baseName(batterDest ?? 0)
                }`
              : "Record each runner destination before confirming."}
          </DrawerDescription>
        </DrawerHeader>
        <DrawerBody className="space-y-3">
          {[2, 1, 0].map((baseIndex) => {
            const playerId = bases[baseIndex];
            if (!playerId) return null;
            const name =
              playerId === "manual"
                ? "Runner"
                : players.find((player) => player.id === playerId)?.name;
            const currentDestination =
              pendingPlay.decisions[baseIndex] ?? baseIndex;
            const options: Array<{
              v: RunnerDestination;
              label: string;
            }> = [
              { v: baseIndex, label: "HOLD" },
              ...(baseIndex + 1 <= 2
                ? [
                    {
                      v: baseIndex + 1,
                      label: baseName(baseIndex + 1).toUpperCase(),
                    },
                  ]
                : []),
              ...(baseIndex + 2 <= 2
                ? [
                    {
                      v: baseIndex + 2,
                      label: baseName(baseIndex + 2).toUpperCase(),
                    },
                  ]
                : []),
              { v: "H", label: "🏠 SCORES" },
              { v: "X", label: "OUT" },
            ];

            return (
              <section key={baseIndex}>
                <div className="mb-1.5 flex items-baseline gap-1.5">
                  <span className="truncate font-bold">{name}</span>
                  <span className="text-xs text-foreground-muted">
                    on {baseName(baseIndex)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 sm:flex">
                  {options.map((option) => {
                    const selected = currentDestination === option.v;
                    return (
                      <Button
                        key={String(option.v)}
                        type="button"
                        size="sm"
                        variant={
                          selected
                            ? option.v === "X"
                              ? "destructive"
                              : "selected"
                            : option.v === "X"
                              ? "scoringOut"
                              : "outline"
                        }
                        onClick={() => onSetDecision(baseIndex, option.v)}
                        disabled={submitting}
                        aria-pressed={selected}
                        className="min-w-0"
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
              </section>
            );
          })}

          <section className="flex items-center gap-2 border-t border-border pt-3">
            <Badge variant="default">RBI</Badge>
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label="−"
              onClick={() => onAdjustPendingRbi(-1)}
              disabled={submitting}
            >
              <Minus aria-hidden="true" />
            </Button>
            <output className="min-w-8 text-center font-mono text-xl font-bold">
              {pendingPlay.rbi}
            </output>
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label="+"
              onClick={() => onAdjustPendingRbi(1)}
              disabled={submitting}
            >
              <Plus aria-hidden="true" />
            </Button>
          </section>

          {draftValidation.message ? (
            <div
              role="alert"
              className="rounded-[var(--radius-sm)] border border-danger/60 bg-danger/10 px-3 py-2 text-sm text-danger"
            >
              ⚠ {draftValidation.message}
            </div>
          ) : null}
        </DrawerBody>
        <DrawerFooter>
          <Button
            type="button"
            size="default"
            className="min-w-0 flex-[2]"
            disabled={collision}
            pending={submitting}
            pendingLabel="CONFIRMING…"
            onClick={confirmPlay}
          >
            ✓ CONFIRM PLAY
          </Button>
          <DrawerClose asChild>
            <Button
              type="button"
              size="default"
              variant="outline"
              className="flex-1"
              disabled={submitting}
            >
              CANCEL
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export function PlayLog({ log, open, onToggle }: PlayLogProps) {
  if (log.length === 0) return null;

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "mt-2 block w-full rounded-[var(--radius-sm)] border border-border/70 bg-surface/55 px-2.5 py-1.5 text-left font-mono text-xs text-foreground-muted outline-none hover:bg-surface focus-visible:ring-2 focus-visible:ring-ring/70",
        open && "max-h-36 overflow-y-auto",
      )}
    >
      {(open ? log : log.slice(0, 1)).map((line, index) => (
        <span key={index} className="block truncate">
          · {line}
          {!open && log.length > 1 ? ` (+${log.length - 1} ▸)` : ""}
        </span>
      ))}
      <span className="sr-only">
        {open ? "Collapse play log" : "Expand play log"}
      </span>
      {!open && log.length === 1 ? (
        <ChevronRight
          className="ml-1 inline size-3 align-[-0.1em]"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
}
